import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from './config';
import { formatLocalDateTime } from './timezone';

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  padding: '20px',
  boxSizing: 'border-box'
};

const contentStyle = {
  maxWidth: '1180px',
  margin: '0 auto',
  width: '100%',
  background: 'var(--bg-tertiary)',
  padding: '22px',
  borderRadius: '8px',
  boxShadow: '0 4px 6px var(--shadow-color)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden'
};

const inputStyle = {
  padding: '8px 10px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px'
};

const buttonStyle = {
  padding: '8px 14px',
  backgroundColor: 'var(--accent-color)',
  color: 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px'
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--button-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)'
};

const smallButtonStyle = {
  padding: '4px 8px',
  backgroundColor: 'var(--button-bg)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '11px'
};

const textDangerButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--error-color)',
  cursor: 'pointer',
  fontSize: '11px',
  padding: '4px 6px',
  opacity: 0.8
};

// Friendly labels for column types
const TYPE_LABELS = {
  'text': 'Text',
  'number': 'Number',
  'boolean': 'True/False',
  'date': 'Date',
  'select': 'Dropdown',
  'url': 'URL',
  'file-reference': 'File'
};

const TABLE_COLUMN_TYPES = ['text', 'number', 'boolean', 'date', 'select', 'url', 'file-reference'];

function parseJsonText(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function normalizeSchemaColumns(schema) {
  if (!Array.isArray(schema)) return [];
  return schema
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => ({
      id: item.id || `col-${index + 1}-${Date.now()}`,
      name: String(item.name || '').trim(),
      type: TABLE_COLUMN_TYPES.includes(String(item.type || '').toLowerCase()) ? String(item.type || '').toLowerCase() : 'text',
      required: Boolean(item.required),
      optionsText: Array.isArray(item.options) ? item.options.join(', ') : ''
    }));
}

function toSchemaPayload(columns) {
  return columns
    .map((col) => {
      const payload = {
        name: String(col.name || '').trim(),
        type: String(col.type || 'text').toLowerCase(),
        required: Boolean(col.required)
      };
      if (payload.type === 'select') {
        payload.options = String(col.optionsText || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return payload;
    })
    .filter((col) => col.name);
}

function parseInputByType(raw, type) {
  if (type === 'number') {
    if (raw === '') return null;
    const num = Number(raw);
    return Number.isNaN(num) ? raw : num;
  }
  if (type === 'boolean') return raw === 'true';
  if (type === 'file-reference') {
    const parsed = parseJsonText(raw, undefined);
    if (typeof parsed === 'string' || (parsed && typeof parsed === 'object')) return parsed;
    return raw;
  }
  return raw;
}

function formatValueForInput(value, type) {
  if (value === null || value === undefined) return type === 'boolean' ? 'false' : '';
  if (type === 'boolean') return String(Boolean(value));
  if (type === 'file-reference' && typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/** Reusable inline schema column editor */
function SchemaEditor({ columns, onChange }) {
  const addColumn = () =>
    onChange([...columns, { id: `col-${columns.length + 1}-${Date.now()}`, name: '', type: 'text', required: false, optionsText: '' }]);

  const updateColumn = (id, patch) =>
    onChange(columns.map((col) => (col.id === id ? { ...col, ...patch } : col)));

  const removeColumn = (id) =>
    onChange(columns.filter((col) => col.id !== id));

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px', background: 'var(--bg-secondary)', display: 'grid', gap: '8px' }}>
      {columns.map((col) => (
        <div key={col.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 120px auto', gap: '6px', alignItems: 'center' }}>
          <input
            value={col.name}
            onChange={(e) => updateColumn(col.id, { name: e.target.value })}
            style={inputStyle}
            placeholder="Column name"
          />
          <select
            value={col.type}
            onChange={(e) => updateColumn(col.id, { type: e.target.value })}
            style={inputStyle}
          >
            {TABLE_COLUMN_TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
            ))}
          </select>
          <button
            style={{ ...textDangerButtonStyle, fontSize: '16px', lineHeight: 1 }}
            onClick={() => removeColumn(col.id)}
            disabled={columns.length <= 1}
            title="Remove column"
          >×</button>
          <label style={{ gridColumn: '1 / span 2', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={Boolean(col.required)}
              onChange={(e) => updateColumn(col.id, { required: e.target.checked })}
            />
            Required
          </label>
          {col.type === 'select' && (
            <input
              value={col.optionsText}
              onChange={(e) => updateColumn(col.id, { optionsText: e.target.value })}
              style={{ ...inputStyle, gridColumn: '1 / span 2' }}
              placeholder="Options (comma-separated): New, In Progress, Done"
            />
          )}
        </div>
      ))}
      <button style={secondaryButtonStyle} onClick={addColumn}>+ Add Column</button>
    </div>
  );
}

export default function Tables({ onBack, currentWorkspaceId, currentWorkspaceRole }) {
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Scope / workspace
  const [scope, setScope] = useState(currentWorkspaceId ? 'workspace' : 'personal');
  const [workspaceId, setWorkspaceId] = useState(currentWorkspaceId || '');
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  // Tables list
  const [tables, setTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Create table form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tableFormName, setTableFormName] = useState('');
  const [schemaColumns, setSchemaColumns] = useState([
    { id: `col-1-${Date.now()}`, name: 'name', type: 'text', required: false, optionsText: '' }
  ]);
  const [savingTable, setSavingTable] = useState(false);

  // Edit schema
  const [editingSchemaTableId, setEditingSchemaTableId] = useState(null);
  const [editingSchemaColumns, setEditingSchemaColumns] = useState([]);
  const [savingSchema, setSavingSchema] = useState(false);

  // Rows
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [rowsOffset, setRowsOffset] = useState(0);

  // Row form
  const [showAddRowForm, setShowAddRowForm] = useState(false);
  const [rowFormId, setRowFormId] = useState(null);
  const [rowFormData, setRowFormData] = useState('{\n}');
  const [rowFormValues, setRowFormValues] = useState({});
  const [savingRow, setSavingRow] = useState(false);

  // Filter / sort
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [sortColumn, setSortColumn] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // CSV
  const [importingCsv, setImportingCsv] = useState(false);
  const importInputRef = useRef(null);
  const rowFormRef = useRef(null);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedTableId) || null,
    [tables, selectedTableId]
  );

  const selectedWorkspaceRole = useMemo(() => {
    if (!workspaceId) return currentWorkspaceRole;
    const ws = workspaces.find((w) => w.id === workspaceId);
    return ws?.my_role || currentWorkspaceRole;
  }, [workspaces, workspaceId, currentWorkspaceRole]);

  const canWriteWorkspace = selectedWorkspaceRole === 'admin' || selectedWorkspaceRole === 'editor';
  const canWrite = scope === 'personal' || canWriteWorkspace;
  const selectedTableSchema = Array.isArray(selectedTable?.schema) ? selectedTable.schema : [];

  const columnNames = useMemo(() => {
    const schemaCols = Array.isArray(selectedTable?.schema)
      ? selectedTable.schema.map((c) => c?.name).filter((n) => typeof n === 'string' && n.trim())
      : [];
    const extra = new Set();
    rows.forEach((row) => Object.keys(row?.data || {}).forEach((k) => extra.add(k)));
    return Array.from(new Set([...schemaCols, ...Array.from(extra)]));
  }, [rows, selectedTable]);

  const displayedRows = useMemo(() => {
    const normalizedFilter = filterValue.trim().toLowerCase();
    let next = [...rows];
    if (filterColumn && normalizedFilter) {
      next = next.filter((row) => {
        const v = row?.data?.[filterColumn];
        if (v === null || v === undefined) return false;
        return String(v).toLowerCase().includes(normalizedFilter);
      });
    }
    next.sort((a, b) => {
      let av, bv;
      if (sortColumn === 'created_at' || sortColumn === 'updated_at') {
        av = a?.[sortColumn] || '';
        bv = b?.[sortColumn] || '';
      } else {
        av = a?.data?.[sortColumn];
        bv = b?.data?.[sortColumn];
      }
      const at = av === null || av === undefined ? '' : String(av).toLowerCase();
      const bt = bv === null || bv === undefined ? '' : String(bv).toLowerCase();
      if (at < bt) return sortDirection === 'asc' ? -1 : 1;
      if (at > bt) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return next;
  }, [rows, filterColumn, filterValue, sortColumn, sortDirection]);

  // Sync rowFormData with rowFormValues
  useEffect(() => {
    const data = {};
    Object.entries(rowFormValues || {}).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) data[k] = v;
    });
    setRowFormData(JSON.stringify(data, null, 2));
  }, [rowFormValues]);

  // Reset row form when table changes
  useEffect(() => {
    if (!selectedTable) return;
    const schema = Array.isArray(selectedTable.schema) ? selectedTable.schema : [];
    const next = {};
    schema.forEach((col) => { if (col?.name) next[col.name] = col.type === 'boolean' ? false : ''; });
    setRowFormId(null);
    setRowFormValues(next);
    setRowFormData(JSON.stringify({}, null, 2));
    setShowAddRowForm(false);
  }, [selectedTable?.id]);

  // ── API ──────────────────────────────────────────────────────────────────────

  const loadWorkspaces = useCallback(async () => {
    setWorkspacesLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/workspaces/`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to load workspaces');
      const list = await res.json();
      setWorkspaces(Array.isArray(list) ? list : []);
      if (!currentWorkspaceId && !workspaceId && list.length > 0) setWorkspaceId(list[0].id);
    } catch {
      setWorkspaces([]);
    } finally {
      setWorkspacesLoading(false);
    }
  }, [token, currentWorkspaceId, workspaceId]);

  const loadTables = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('scope', scope);
      if (scope === 'workspace' && workspaceId) params.set('workspace_id', workspaceId);
      const res = await fetch(`${API_BASE_URL}/tables?${params}`, { headers: authHeaders });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to load tables');
      }
      const payload = await res.json();
      const list = Array.isArray(payload) ? payload : [];
      setTables(list);
      if (!selectedTableId && list.length > 0) setSelectedTableId(list[0].id);
      if (selectedTableId && !list.find((t) => t.id === selectedTableId)) setSelectedTableId(list[0]?.id || null);
    } catch (e) {
      setError(e.message || 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  }, [scope, workspaceId, token, selectedTableId]);

  const loadRows = useCallback(async (tableId, offset = 0) => {
    if (!tableId) { setRows([]); setRowsTotal(0); return; }
    setError('');
    try {
      const params = new URLSearchParams({ limit: '100', offset: String(offset) });
      const res = await fetch(`${API_BASE_URL}/tables/${tableId}/rows?${params}`, { headers: authHeaders });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to load rows');
      }
      const payload = await res.json();
      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setRowsTotal(Number(payload.total) || 0);
      setRowsOffset(offset);
    } catch (e) {
      setError(e.message || 'Failed to load rows');
    }
  }, [token]);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);
  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { loadRows(selectedTableId, 0); }, [selectedTableId, loadRows]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleCreateTable = async () => {
    if (!canWrite) { setError('You do not have permission to create tables in this scope.'); return; }
    const name = tableFormName.trim();
    if (!name) { setError('Table name is required.'); return; }
    const schema = toSchemaPayload(schemaColumns);
    if (schema.length === 0) { setError('Add at least one column with a name.'); return; }
    setSavingTable(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/tables`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name, scope, workspace_id: scope === 'workspace' ? workspaceId : null, schema })
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to create table');
      }
      const created = await res.json();
      setTableFormName('');
      setSchemaColumns([{ id: `col-1-${Date.now()}`, name: 'name', type: 'text', required: false, optionsText: '' }]);
      setShowCreateForm(false);
      setSelectedTableId(created.id);
      await loadTables();
    } catch (e) {
      setError(e.message || 'Failed to create table');
    } finally {
      setSavingTable(false);
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!tableId || !canWrite) return;
    if (!window.confirm('Delete this table and all its rows? This cannot be undone.')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${tableId}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to delete table');
      }
      if (selectedTableId === tableId) setSelectedTableId(null);
      await loadTables();
    } catch (e) {
      setError(e.message || 'Failed to delete table');
    }
  };

  const startEditSchema = (table) => {
    setEditingSchemaTableId(table.id);
    setEditingSchemaColumns(normalizeSchemaColumns(table.schema));
  };

  const cancelEditSchema = () => {
    setEditingSchemaTableId(null);
    setEditingSchemaColumns([]);
  };

  const handleUpdateSchema = async (tableId) => {
    const schema = toSchemaPayload(editingSchemaColumns);
    if (schema.length === 0) { setError('Add at least one column with a name.'); return; }
    setSavingSchema(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${tableId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ schema })
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to update schema');
      }
      cancelEditSchema();
      await loadTables();
    } catch (e) {
      setError(e.message || 'Failed to update schema');
    } finally {
      setSavingSchema(false);
    }
  };

  const handleSaveRow = async () => {
    if (!selectedTableId) return;
    if (!canWrite) { setError('You do not have permission to edit rows in this scope.'); return; }
    let rowData = {};
    if (selectedTableSchema.length > 0) {
      Object.entries(rowFormValues || {}).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) rowData[k] = v;
      });
    } else {
      const parsed = parseJsonText(rowFormData, null);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { setError('Row data must be a JSON object.'); return; }
      rowData = parsed;
    }
    setSavingRow(true);
    setError('');
    try {
      const endpoint = rowFormId
        ? `${API_BASE_URL}/tables/${selectedTableId}/rows/${rowFormId}`
        : `${API_BASE_URL}/tables/${selectedTableId}/rows`;
      const res = await fetch(endpoint, {
        method: rowFormId ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify({ data: rowData })
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to save row');
      }
      setRowFormId(null);
      setShowAddRowForm(false);
      const next = {};
      selectedTableSchema.forEach((col) => { if (col?.name) next[col.name] = col.type === 'boolean' ? false : ''; });
      setRowFormValues(next);
      await loadRows(selectedTableId, rowsOffset);
    } catch (e) {
      setError(e.message || 'Failed to save row');
    } finally {
      setSavingRow(false);
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!selectedTableId || !rowId || !canWrite) return;
    if (!window.confirm('Delete this row?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${selectedTableId}/rows/${rowId}`, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to delete row');
      }
      await loadRows(selectedTableId, rowsOffset);
    } catch (e) {
      setError(e.message || 'Failed to delete row');
    }
  };

  const handleEditRow = (row) => {
    setRowFormId(row.id);
    const rowData = row?.data || {};
    const next = {};
    selectedTableSchema.forEach((col) => {
      if (!col?.name) return;
      const cur = rowData[col.name];
      next[col.name] = (cur === undefined || cur === null) ? (col.type === 'boolean' ? false : '') : cur;
    });
    Object.keys(rowData).forEach((k) => { if (next[k] === undefined) next[k] = rowData[k]; });
    setRowFormValues(next);
    setRowFormData(JSON.stringify(rowData, null, 2));
    setShowAddRowForm(true);
    setTimeout(() => rowFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleExportCsv = async () => {
    if (!selectedTableId) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${selectedTableId}/rows/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to export CSV');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTable?.name || 'table'}_rows.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to export CSV');
    }
  };

  const handleImportCsv = async () => {
    if (!selectedTableId || !canWrite) return;
    const file = importInputRef.current?.files?.[0];
    if (!file) { setError('Please choose a CSV file to import.'); return; }
    setImportingCsv(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/tables/${selectedTableId}/rows/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.detail || 'Failed to import CSV');
      }
      if (importInputRef.current) importInputRef.current.value = '';
      await loadRows(selectedTableId, rowsOffset);
    } catch (e) {
      setError(e.message || 'Failed to import CSV');
    } finally {
      setImportingCsv(false);
    }
  };

  const cancelRowEdit = () => {
    setRowFormId(null);
    setShowAddRowForm(false);
    const next = {};
    selectedTableSchema.forEach((col) => { if (col?.name) next[col.name] = col.type === 'boolean' ? false : ''; });
    setRowFormValues(next);
    setRowFormData(JSON.stringify({}, null, 2));
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const paginationStart = rowsOffset + 1;
  const paginationEnd = rowsOffset + rows.length;

  return (
    <div style={containerStyle}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px', maxWidth: '1180px', margin: '0 auto', width: '100%', paddingBottom: '12px' }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '22px', cursor: 'pointer', marginRight: '12px' }}>←</button>
        )}
        <h2 style={{ margin: 0, fontSize: '20px' }}>📋 Tables</h2>
      </div>

      <div style={contentStyle}>
        {/* Top toolbar: scope, workspace, refresh, export/import */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px', flexShrink: 0 }}>
          <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ ...inputStyle, width: '150px' }}>
            <option value="workspace">Workspace</option>
            <option value="personal">Personal</option>
          </select>
          <select
            value={workspaceId}
            disabled={scope !== 'workspace'}
            onChange={(e) => setWorkspaceId(e.target.value)}
            style={{ ...inputStyle, width: '240px', opacity: scope !== 'workspace' ? 0.65 : 1 }}
          >
            <option value="">{workspacesLoading ? 'Loading…' : 'Select workspace'}</option>
            {workspaces.map((ws) => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
          </select>
          <button style={secondaryButtonStyle} onClick={loadTables} disabled={loading}>Refresh</button>
          {selectedTableId && (
            <button style={secondaryButtonStyle} onClick={handleExportCsv}>Export CSV</button>
          )}
          {selectedTableId && canWrite && (
            <>
              <input ref={importInputRef} type="file" accept=".csv,text/csv" style={{ ...inputStyle, width: '200px', padding: '6px 8px' }} />
              <button style={secondaryButtonStyle} onClick={handleImportCsv} disabled={importingCsv}>
                {importingCsv ? 'Importing…' : 'Import CSV'}
              </button>
            </>
          )}
        </div>

        {error && (
          <div style={{ background: 'var(--error-bg, #3a1a1a)', color: 'var(--error-color)', padding: '8px 12px', borderRadius: '4px', marginBottom: '10px', fontSize: '13px', flexShrink: 0 }}>
            {error}
          </div>
        )}

        {/* Main two-column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '16px', minHeight: 0, flex: 1, overflow: 'hidden' }}>

          {/* LEFT: table list */}
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Header + New Table toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', fontWeight: 700 }}>Tables ({tables.length})</span>
              {canWrite && (
                <button
                  style={{ ...smallButtonStyle, background: showCreateForm ? 'var(--accent-color)' : undefined, color: showCreateForm ? '#fff' : undefined }}
                  onClick={() => setShowCreateForm((v) => !v)}
                >
                  {showCreateForm ? '✕ Cancel' : '+ New Table'}
                </button>
              )}
            </div>

            {/* Create table form (collapsible) */}
            {showCreateForm && (
              <div style={{ border: '1px solid var(--accent-color)', borderRadius: '6px', padding: '10px', background: 'var(--bg-secondary)', display: 'grid', gap: '8px' }}>
                <input
                  value={tableFormName}
                  onChange={(e) => setTableFormName(e.target.value)}
                  style={inputStyle}
                  placeholder="Table name"
                  autoFocus
                />
                <SchemaEditor columns={schemaColumns} onChange={setSchemaColumns} />
                <button style={buttonStyle} onClick={handleCreateTable} disabled={savingTable}>
                  {savingTable ? 'Creating…' : 'Create Table'}
                </button>
              </div>
            )}

            {/* Table list */}
            <div style={{ display: 'grid', gap: '6px' }}>
              {tables.map((table) => {
                const isSelected = table.id === selectedTableId;
                const isEditingSchema = editingSchemaTableId === table.id;
                return (
                  <div
                    key={table.id}
                    style={{
                      border: `1px solid ${isSelected ? 'var(--accent-color)' : 'var(--border-color)'}`,
                      borderRadius: '6px',
                      padding: '8px 10px',
                      background: isSelected ? 'var(--bg-secondary)' : 'transparent'
                    }}
                  >
                    {/* Table name + row count */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                      <button
                        onClick={() => setSelectedTableId(table.id)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: '13px', textAlign: 'left', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {table.name}
                      </button>
                      {isSelected && rowsTotal > 0 && (
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', flexShrink: 0 }}>{rowsTotal} rows</span>
                      )}
                    </div>

                    {/* Schema summary */}
                    {Array.isArray(table.schema) && table.schema.length > 0 && !isEditingSchema && (
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {table.schema.map((c) => c.name).filter(Boolean).join(', ')}
                      </div>
                    )}

                    {/* Action row */}
                    {!isEditingSchema && canWrite && (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                        <button style={smallButtonStyle} onClick={() => startEditSchema(table)}>Edit Schema</button>
                        <button style={textDangerButtonStyle} onClick={() => handleDeleteTable(table.id)}>Delete</button>
                      </div>
                    )}

                    {/* Inline schema editor */}
                    {isEditingSchema && (
                      <div style={{ marginTop: '8px', display: 'grid', gap: '8px' }}>
                        <SchemaEditor columns={editingSchemaColumns} onChange={setEditingSchemaColumns} />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button style={{ ...buttonStyle, fontSize: '12px', padding: '5px 10px' }} onClick={() => handleUpdateSchema(table.id)} disabled={savingSchema}>
                            {savingSchema ? 'Saving…' : 'Save Schema'}
                          </button>
                          <button style={{ ...secondaryButtonStyle, fontSize: '12px', padding: '5px 10px' }} onClick={cancelEditSchema}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {tables.length === 0 && !loading && (
                <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '8px 0' }}>
                  No tables yet.{canWrite ? ' Click "+ New Table" to create one.' : ''}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: rows panel */}
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', minHeight: 0, gap: '10px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>
                {selectedTable ? selectedTable.name : 'Select a table'}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedTable && rowsTotal > 0 && (
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {rowsTotal} row{rowsTotal !== 1 ? 's' : ''}
                  </span>
                )}
                {selectedTable && canWrite && (
                  <button
                    style={{ ...buttonStyle, fontSize: '12px', padding: '5px 12px' }}
                    onClick={() => {
                      if (showAddRowForm && rowFormId) {
                        cancelRowEdit();
                      } else {
                        setShowAddRowForm((v) => !v);
                        if (!showAddRowForm) setRowFormId(null);
                      }
                    }}
                  >
                    {showAddRowForm ? '✕ Cancel' : '+ Add Row'}
                  </button>
                )}
              </div>
            </div>

            {/* Filter + sort bar */}
            {selectedTable && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0, padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: 600 }}>Filter:</span>
                <select value={filterColumn} onChange={(e) => setFilterColumn(e.target.value)} style={{ ...inputStyle, width: '160px', fontSize: '12px', padding: '5px 8px' }}>
                  <option value="">All columns</option>
                  {columnNames.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  style={{ ...inputStyle, width: '180px', fontSize: '12px', padding: '5px 8px' }}
                  placeholder="Search value…"
                />
                <span style={{ color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: 600, marginLeft: '4px' }}>Sort:</span>
                <select value={sortColumn} onChange={(e) => setSortColumn(e.target.value)} style={{ ...inputStyle, width: '160px', fontSize: '12px', padding: '5px 8px' }}>
                  <option value="updated_at">Last Updated</option>
                  <option value="created_at">Created Date</option>
                  {columnNames.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)} style={{ ...inputStyle, width: '110px', fontSize: '12px', padding: '5px 8px' }}>
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>
            )}

            {/* Data grid */}
            <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {selectedTable && displayedRows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr>
                      {columnNames.map((col) => (
                        <th key={col} style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color)', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{col}</th>
                      ))}
                      <th style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color)', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>Last Updated</th>
                      {canWrite && (
                        <th style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color)', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 600 }}></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRows.map((row) => (
                      <tr key={row.id} style={{ background: row.id === rowFormId ? 'var(--bg-secondary)' : 'transparent' }}>
                        {columnNames.map((col) => (
                          <td key={`${row.id}-${col}`} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '280px' }}>
                            {(() => {
                              const v = row?.data?.[col];
                              if (v === null || v === undefined) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                              if (typeof v === 'boolean') return v ? '✓' : '✗';
                              return typeof v === 'object' ? JSON.stringify(v) : String(v);
                            })()}
                          </td>
                        ))}
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', fontSize: '11px' }}>
                          {row.updated_at ? formatLocalDateTime(row.updated_at) : '—'}
                        </td>
                        {canWrite && (
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-color)', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button style={smallButtonStyle} onClick={() => handleEditRow(row)}>Edit</button>
                              <button style={textDangerButtonStyle} onClick={() => handleDeleteRow(row.id)}>Delete</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {selectedTable && displayedRows.length === 0 && (
                <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>
                  {rows.length === 0 ? 'No rows yet. Click "+ Add Row" to add your first entry.' : 'No rows match the current filter.'}
                </div>
              )}
              {!selectedTable && (
                <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>
                  Select a table from the left to view its data.
                </div>
              )}
            </div>

            {/* Pagination */}
            {selectedTable && rowsTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  Showing {paginationStart}–{paginationEnd} of {rowsTotal} rows
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={secondaryButtonStyle} disabled={rowsOffset <= 0} onClick={() => loadRows(selectedTableId, Math.max(0, rowsOffset - 100))}>← Previous</button>
                  <button style={secondaryButtonStyle} disabled={rowsOffset + rows.length >= rowsTotal} onClick={() => loadRows(selectedTableId, rowsOffset + 100)}>Next →</button>
                </div>
              </div>
            )}

            {/* Add / Edit row form (collapsible, at bottom) */}
            {showAddRowForm && selectedTable && (
              <div ref={rowFormRef} style={{ borderTop: '2px solid var(--accent-color)', paddingTop: '12px', flexShrink: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>
                  {rowFormId ? '✏️ Edit Row' : '➕ New Row'}
                </div>
                <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                  {selectedTableSchema.length > 0 ? (
                    selectedTableSchema.map((col) => {
                      const name = String(col?.name || '');
                      if (!name) return null;
                      const type = String(col?.type || 'text').toLowerCase();
                      const value = formatValueForInput(rowFormValues[name], type);
                      if (type === 'boolean') {
                        return (
                          <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(rowFormValues[name])}
                              onChange={(e) => setRowFormValues((prev) => ({ ...prev, [name]: e.target.checked }))}
                            />
                            {name}
                          </label>
                        );
                      }
                      if (type === 'select') {
                        const options = Array.isArray(col.options) ? col.options : [];
                        return (
                          <div key={name} style={{ display: 'grid', gap: '4px' }}>
                            <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {name}{col.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
                            </label>
                            <select value={value} onChange={(e) => setRowFormValues((prev) => ({ ...prev, [name]: e.target.value }))} style={inputStyle}>
                              <option value="">Select {name}…</option>
                              {options.map((o) => <option key={`${name}-${o}`} value={String(o)}>{String(o)}</option>)}
                            </select>
                          </div>
                        );
                      }
                      return (
                        <div key={name} style={{ display: 'grid', gap: '4px' }}>
                          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {name}
                            <span style={{ color: 'var(--text-tertiary)', marginLeft: '4px' }}>({TYPE_LABELS[type] || type})</span>
                            {col.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
                          </label>
                          <input
                            type={type === 'number' ? 'number' : type === 'date' ? 'date' : type === 'url' ? 'url' : 'text'}
                            value={value}
                            onChange={(e) => setRowFormValues((prev) => ({ ...prev, [name]: parseInputByType(e.target.value, type) }))}
                            style={inputStyle}
                            placeholder={type === 'file-reference' ? 'URL or JSON object' : ''}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <textarea
                      value={rowFormData}
                      onChange={(e) => setRowFormData(e.target.value)}
                      style={{ ...inputStyle, minHeight: '110px', resize: 'vertical', fontFamily: 'monospace' }}
                      placeholder='{"key": "value"}'
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={buttonStyle} onClick={handleSaveRow} disabled={savingRow}>
                    {savingRow ? 'Saving…' : rowFormId ? 'Update Row' : 'Add Row'}
                  </button>
                  <button style={secondaryButtonStyle} onClick={cancelRowEdit}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
